import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import SuperLinkEditFormContainer from '../components/SuperLinkEditFormContainer';
import { growlErrors, growlActionSuccess } from '../../../utils/NotificationUtil';
import { updateSuperLink as updateSuperLinkMutation } from '../../../services/apps/SuperLinkService';
import { searchJobProfile } from '../../../services/apps/JobProfileService';
import { NotificationContext } from '../../../components/GrowlNotifications';
import { AccountContext } from '../../../components/AccountContext';
import { defineMessages, useIntl } from 'react-intl';
import useCustomFunctionalityCheck from '../../../hooks/useCustomFunctionalityCheck';
import { CustomFunctionality } from '../../../shared/Constants';

const messages = defineMessages({
  successNoti: {
    id: 'noti.success',
    defaultMessage: 'Success!',
  },
});

const SuperLinkEdit = ({ superLink, toggleEdit, languageList }) => {
  const [loadingJob, setLoadingJob] = useState(false);
  const [jobs, setJobs] = useState([]);

  const { formatMessage } = useIntl();
  const currentAccount = useContext(AccountContext);
  const growl = useContext(NotificationContext);
  const { aid } = useParams();

  const {
    doesAccountHaveAccessToCustomFunctionality,
    getReportsToAutomaticallyGenerate,
  } = useCustomFunctionalityCheck();
  const reportsToAutomaticallyGenerate = getReportsToAutomaticallyGenerate();

  const accountCanSelectMatchPercentageForCareerCounselingReports = doesAccountHaveAccessToCustomFunctionality(
    CustomFunctionality.MINIMUM_MATCH_PERCENTAGE_CC_REPORTS,
  );

  const { assessmentTemplate } = superLink;

  useEffect(() => {
    fetchJobProfiles();
    // eslint-disable-next-line
  }, []);

  const searchJobProfiles = (query) => {
    fetchJobProfiles(query);
  };

  const fetchJobProfiles = async (query) => {
    setLoadingJob(true);

    try {
      const data = await searchJobProfile({
        aid,
        query,
        tempId: assessmentTemplate ? assessmentTemplate.id : null,
      });

      setJobs(data);
    } catch (err) {
      growlErrors(growl, err);
    } finally {
      setLoadingJob(false);
    }
  };

  const updateSuperLink = async (formValues) => {
    const {
      description,
      notes,
      notificationEmails,
      reports,
      locale,
      targetJobId: selectedJob,
      superlinkName,
      sendRespondentReport,
      printFriendlyReport,
      sendRespondentCcInterestsReport,
      sendRespondentCcPersonalityReport,
      enabled,
      activationType,
      numberOfAssessmentsToDisableAfter,
      ccInterestsMinimumMatchPercentage,
      ccPersonalityMinimumMatchPercentage,
    } = formValues;

    const targetJobId = typeof selectedJob === 'object' ? selectedJob.id : selectedJob;

    const input = {
      accountId: currentAccount,
      name: superlinkName,
      superLinkId: superLink.id,
      description,
      notes,
      notificationEmails,
      reports,
      locale,
      targetJobId,
      sendRespondentReport: !!sendRespondentReport,
      sendRespondentCcInterestsReport: !!sendRespondentCcInterestsReport,
      sendRespondentCcPersonalityReport: !!sendRespondentCcPersonalityReport,
      ccInterestsMinimumMatchPercentage,
      ccPersonalityMinimumMatchPercentage,
      printFriendlyReport: !!printFriendlyReport,
      activationOptionsInput: {
        enabled,
        activationType,
        numberOfAssessmentsToDisableAfter: activationType === 'MANUAL' ? null : numberOfAssessmentsToDisableAfter,
      },
    };
    try {
      await updateSuperLinkMutation(input);
      growlActionSuccess(growl, formatMessage(messages.successNoti));
      toggleEdit('core');
    } catch (errors) {
      growlErrors(growl, errors);
    }
  };

  const handleCancel = () => {
    toggleEdit('core');
    return Promise.resolve();
  };

  return (
    <SuperLinkEditFormContainer
      superLink={superLink}
      searching={loadingJob}
      searchJob={searchJobProfiles}
      toggleEdit={toggleEdit}
      locale={languageList}
      listJobProfiles={jobs}
      updateSuperLink={updateSuperLink}
      reportForDropDown={reportsToAutomaticallyGenerate}
      handleCancel={handleCancel}
      accountCanSelectMatchPercentageForCareerCounselingReports={
        accountCanSelectMatchPercentageForCareerCounselingReports
      }
    />
  );
};

SuperLinkEdit.propTypes = {
  superLink: PropTypes.objectOf(PropTypes.any).isRequired,
  languageList: PropTypes.arrayOf(PropTypes.any).isRequired,
  toggleEdit: PropTypes.func.isRequired,
};

export default SuperLinkEdit;
